import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

function ChargerStatsCard({ color, title, count, icon, onClick }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Color mapping for gradients
  const colorGradients = {
    dark: "linear-gradient(195deg, #42424a, #191919)",
    success: "linear-gradient(195deg, #66BB6A, #43A047)",
    error: "linear-gradient(195deg, #EF5350, #E53935)",
    info: "linear-gradient(195deg, #49a3f1, #1A73E8)",
    warning: "linear-gradient(195deg, #FFA726, #FB8C00)",
  };

  const hoverGradients = {
    dark: "linear-gradient(195deg, #525252, #2a2a2a)",
    success: "linear-gradient(195deg, #7BC67E, #4CAF50)",
    error: "linear-gradient(195deg, #F44336, #D32F2F)",
    info: "linear-gradient(195deg, #64B5F6, #2196F3)",
    warning: "linear-gradient(195deg, #FFB74D, #F57C00)",
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.3s cubic-bezier(0.34, 1.61, 0.7, 1)",
        "&:hover": onClick
          ? {
              transform: "translateY(-8px) scale(1.02)",
              boxShadow: darkMode
                ? "0 12px 24px rgba(0, 0, 0, 0.5)"
                : "0 12px 24px rgba(0, 0, 0, 0.15)",
            }
          : {},
      }}
    >
      <MDBox p={3} display="flex" alignItems="center" gap={3}>
        {/* Icon Container */}
        <MDBox
          sx={{
            width: 72,
            height: 72,
            borderRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: colorGradients[color] || colorGradients.info,
            boxShadow: darkMode
              ? "0 8px 24px rgba(0, 0, 0, 0.4)"
              : "0 8px 24px rgba(0, 0, 0, 0.15)",
            transition: "all 0.3s ease",
            flexShrink: 0,
            ...(onClick && {
              "&:hover": {
                background: hoverGradients[color] || hoverGradients.info,
                transform: "rotate(5deg) scale(1.05)",
              },
            }),
          }}
        >
          {icon ? (
            <img
              src={icon}
              alt={title}
              style={{
                width: "40px",
                height: "40px",
                objectFit: "contain",
                filter: "brightness(0) invert(1)",
              }}
            />
          ) : null}
        </MDBox>

        {/* Content */}
        <MDBox flex={1}>
          <MDTypography
            variant="button"
            fontWeight="regular"
            color="text"
            sx={{
              opacity: 0.8,
              textTransform: "uppercase",
              fontSize: "0.75rem",
              letterSpacing: "0.5px",
              mb: 0.5,
            }}
          >
            {title}
          </MDTypography>
          <MDTypography
            variant="h2"
            fontWeight="bold"
            sx={{
              background: colorGradients[color] || colorGradients.info,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontSize: "2.5rem",
            }}
          >
            {count}
          </MDTypography>
        </MDBox>

        {/* Decorative Element */}
        <MDBox
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "120px",
            height: "120px",
            borderRadius: "0 16px 0 100%",
            background: `${colorGradients[color] || colorGradients.info}`,
            opacity: 0.1,
            pointerEvents: "none",
          }}
        />
      </MDBox>
    </Card>
  );
}

ChargerStatsCard.defaultProps = {
  color: "info",
  onClick: null,
};

ChargerStatsCard.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.string,
  onClick: PropTypes.func,
};

export default ChargerStatsCard;
